import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { mergeFunctionKeyHeaders } from "../../axios/functionKey";

// Async thunk to fetch table IDs for a given jobId from multiple API URLs
export const fetchTableIds = createAsyncThunk(
  "tables/fetchTableIds",
  async ({ jobId, apiUrls }, { rejectWithValue, dispatch, getState }) => {
    try {
      const tableIds = {}; // This will store the table IDs with zone labels for each table

      // Loop through the provided API URLs (apiUrls is an object with table names as keys)
      for (const [tableName, apiUrl] of Object.entries(apiUrls)) {
        const response = await fetch(apiUrl, {
          method: "GET",
          headers: mergeFunctionKeyHeaders({
            "Content-Type": "application/json",
          }, apiUrl),
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch data for ${tableName}: ${response.status}`);
        }

        const data = await response.json();

        // Ensure the result is always an array, even if only a single object is returned
        const matchingItems = Array.isArray(data)
          ? data.filter((item) => String(item.ProjectID) === String(jobId)) 
          : [data].filter((item) => String(item.ProjectID) === String(jobId));

        // Store the IDs with zone labels under the corresponding table name
        tableIds[tableName] = matchingItems.map((item) => {
          return {
            zone: item.Zone, // Assuming "Zone" is the property that contains the zone information
            id: item.ID,
          };
        });
      }

      return tableIds;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch table IDs");
    }
  }
);

// Slice definition
const tableIdSlice = createSlice({
  name: "tables",
  initialState: {
    jobId: null,
    tableIds: {}, // This will store arrays of table IDs with zone labels for different tables
    loading: false,
    error: null,
  },
  reducers: {
    setJobId: (state, action) => {
      state.jobId = action.payload;
    },
    clearTableIds: (state) => {
      state.tableIds = {}; // Reset the table IDs
      state.error = null;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTableIds.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTableIds.fulfilled, (state, action) => {
        state.loading = false;
        state.tableIds = {
          ...state.tableIds,
          ...action.payload,
        };
      })
      .addCase(fetchTableIds.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch table IDs";
      });
  },
});

export const { setJobId, clearTableIds } = tableIdSlice.actions;
export default tableIdSlice.reducer;
